import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { del, get, getErrorMessage, patch, post } from '../api/client';
import { findNextMinistryLeadMeeting, isClosedStatus } from '../utils/hotIssues';
import { toDateStr } from '../utils/meetingsEventsView';

function replaceIssue(list, updated) {
  if (!updated?._id) return list;
  const exists = list.some((item) => item._id === updated._id);
  if (!exists) return [updated, ...list];
  return list.map((item) => (item._id === updated._id ? updated : item));
}

export function useHotIssues(events = null) {
  const eventsProvided = events !== null;
  const [issues, setIssues] = useState([]);
  const [statusOptions, setStatusOptions] = useState([
    'Open', 'In Progress', 'Discussed', 'Parked', 'Resolved', 'Started', 'Hold', 'Completed',
  ]);
  const [fetchedEvents, setFetchedEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const reloadGen = useRef(0);

  const reload = useCallback(async () => {
    const gen = ++reloadGen.current;
    try {
      const { ok, status, data } = await get('/api/meetings/hot-issues');
      if (gen !== reloadGen.current) return ok;
      if (!ok) {
        setError(getErrorMessage({ status, data }, 'Failed to load issues to discuss.'));
        return false;
      }
      setIssues(Array.isArray(data?.issues) ? data.issues : []);
      if (Array.isArray(data?.statusOptions) && data.statusOptions.length) {
        setStatusOptions(data.statusOptions);
      }
      return true;
    } catch (err) {
      if (gen === reloadGen.current) setError('Failed to load issues to discuss.');
      return false;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await reload();
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  useEffect(() => {
    if (eventsProvided) return undefined;
    let cancelled = false;
    const from = toDateStr(new Date());
    const to = `${new Date().getFullYear() + 1}-12-31`;
    (async () => {
      try {
        const { ok, data } = await get(`/api/meetings/events?from=${from}&to=${to}`);
        if (!cancelled && ok && Array.isArray(data)) setFetchedEvents(data);
      } catch (err) {
        // The list still works when the meeting date cannot be loaded.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventsProvided]);

  const nextMeeting = useMemo(
    () => findNextMinistryLeadMeeting(eventsProvided ? events : fetchedEvents),
    [events, eventsProvided, fetchedEvents]
  );

  const addIssue = useCallback(async (payload) => {
    setError('');
    setNotice('');
    if (!String(payload?.issue || '').trim()) {
      setError('Title is required.');
      return false;
    }
    setSubmitting(true);
    try {
      const { ok, status, data } = await post('/api/meetings/hot-issues', payload);
      if (!ok) {
        setError(getErrorMessage({ status, data }, 'Failed to add issue to discuss.'));
        return false;
      }
      setIssues((prev) => replaceIssue(prev, data));
      setNotice('Saved. It is on the list for the next Ministry Lead meeting.');
      await reload();
      return true;
    } catch (err) {
      setError('Failed to add issue to discuss.');
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [reload]);

  const updateIssue = useCallback(async (id, payload) => {
    setError('');
    setNotice('');
    if (payload?.issue !== undefined && !String(payload.issue || '').trim()) {
      setError('Title is required.');
      return false;
    }
    setSubmitting(true);
    try {
      const { ok, status, data } = await patch(`/api/meetings/hot-issues/${id}`, payload);
      if (!ok) {
        setError(getErrorMessage({ status, data }, 'Failed to update issue to discuss.'));
        return false;
      }
      setIssues((prev) => replaceIssue(prev, data));
      setNotice('Issue updated.');
      await reload();
      return true;
    } catch (err) {
      setError('Failed to update issue to discuss.');
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [reload]);

  const setStatus = useCallback(async (item, status) => {
    if (!item?._id || item.status === status) return false;
    setError('');
    setNotice('');
    setSubmitting(true);
    try {
      const { ok, status: httpStatus, data } = await patch(`/api/meetings/hot-issues/${item._id}`, { status });
      if (!ok) {
        setError(getErrorMessage({ status: httpStatus, data }, 'Failed to update status.'));
        return false;
      }
      setIssues((prev) => replaceIssue(prev, data));
      setNotice(isClosedStatus(status)
        ? 'Marked closed. It stays in Closed, and Open brings it back.'
        : `Status set to ${status}.`);
      await reload();
      return true;
    } catch (err) {
      setError('Failed to update status.');
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [reload]);

  const removeIssue = useCallback(async (item) => {
    if (!item?._id) return false;
    setError('');
    setNotice('');
    setSubmitting(true);
    try {
      const { ok, status, data } = await del(`/api/meetings/hot-issues/${item._id}`);
      if (!ok) {
        setError(getErrorMessage({ status, data }, 'Failed to delete issue to discuss.'));
        return false;
      }
      setIssues((prev) => prev.filter((issue) => issue._id !== item._id));
      setNotice('Issue deleted.');
      await reload();
      return true;
    } catch (err) {
      setError('Failed to delete issue to discuss.');
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [reload]);

  const addNote = useCallback(async (item, text) => {
    const note = String(text || '').trim();
    if (!item?._id) return false;
    if (!note) {
      setError('Write a note before saving it.');
      return false;
    }
    setError('');
    setNotice('');
    setSubmitting(true);
    try {
      const { ok, status, data } = await post(`/api/meetings/hot-issues/${item._id}/updates`, { text: note });
      if (!ok) {
        setError(getErrorMessage({ status, data }, 'Failed to add note.'));
        return false;
      }
      setIssues((prev) => replaceIssue(prev, data));
      setNotice('Note added.');
      await reload();
      return true;
    } catch (err) {
      setError('Failed to add note.');
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [reload]);

  const addStatusOption = useCallback(async (onSelect) => {
    const value = window.prompt('Add new Status option:');
    if (!value || !value.trim()) return;
    setError('');
    try {
      const { ok, status, data } = await post('/api/meetings/hot-issues/status-options', { value: value.trim() });
      if (!ok) {
        setError(getErrorMessage({ status, data }, 'Failed to add status.'));
        return;
      }
      if (data?.statusOptions) setStatusOptions(data.statusOptions);
      onSelect?.(value.trim());
    } catch (err) {
      setError('Failed to add status.');
    }
  }, []);

  return {
    issues,
    statusOptions,
    nextMeeting,
    loading,
    submitting,
    error,
    notice,
    addIssue,
    updateIssue,
    setStatus,
    removeIssue,
    addNote,
    addStatusOption,
  };
}
